import React, { useCallback, useEffect, useState } from "react";
import useStaffService from "../../../../services/administration/useStaffService";
import useToast, { ToastType } from "../../../../hooks/useToast";
import { useTranslation } from "react-i18next";
import { Staff } from "../../../../types/Staff";
import SurfaceCard from "../../../../components/Common/SurfaceCard";
import { IconButton, Tooltip, Typography } from "@mui/material";
import StaffDialog, { StaffDialogFields } from "../../../../components/StaffDialog/StaffDialog";
import ChangePasswordDialog from "../../../../components/ChangePasswordDialog";
import { EntityListBox } from "./styled";
import { useAuth } from "../../../../context/AuthContext";
import EntityCard from "../../../../components/EntityCard";
import { stringToHex } from "../../../../utils/hexGenerator";
import { DialogMode } from "../../../../types/DialogTypes";
import AddIcon from '@mui/icons-material/Add';
import { eventBus } from "../../../../common/eventBus";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LockIcon from "@mui/icons-material/Lock";

function Team() {
    const [staffMembers, setStaffMembers] = useState<Staff[]>([]);

    const { fetchAllStaff, deleteStaffMember } = useStaffService();
    const { showToast } = useToast();
    const { t } = useTranslation("common");
    const { activeLocation, userInfo } = useAuth();

    const [staffDialogInfo, setStaffDialogInfo] = useState<{
        isOpen: boolean;
        mode?: DialogMode;
        dialogData?: Partial<StaffDialogFields> & { locationId?: number };
    }>({
        isOpen: false,
        dialogData: {
            locationId: activeLocation?.id,
        }
    });

    const [passwordModalInfo, setPasswordModalInfo] = useState<{
        isOpen: boolean;
        staffMember?: Staff;
    }>({
        isOpen: false,
    });

    const loadTeamMembers = useCallback(() => {
        fetchAllStaff().then((resp) => {
            setStaffMembers(resp[activeLocation?.id ?? 0].staff);
        }).catch(() => {
            setStaffMembers([]);
            showToast({
                message: t("common.unknownError"),
                type: ToastType.ERROR,
            });
        })
    }, [activeLocation?.id, fetchAllStaff, showToast, t]);

    // load team members on mount
    useEffect(() => {
        loadTeamMembers();
    }, [loadTeamMembers]);

    // reload team members on staff changes
    useEffect(() => {
        eventBus.on('staffCreated', loadTeamMembers);
        eventBus.on('staffDeleted', loadTeamMembers);
        eventBus.on('staffEdited', loadTeamMembers);

        return () => {
            eventBus.off('staffCreated', loadTeamMembers);
            eventBus.off('staffDeleted', loadTeamMembers);
            eventBus.off('staffEdited', loadTeamMembers);
        };
    }, [loadTeamMembers]);

    const closeDialog = useCallback(() => setStaffDialogInfo({ isOpen: false }), []);

    const openStaffDialog = useCallback((staffMember: Staff, mode: DialogMode) => {
        setStaffDialogInfo({
            isOpen: true,
            mode,
            dialogData: {
                staffId: staffMember.id,
                name: staffMember.name ?? "",
                phoneNumber: staffMember.phonenumber ?? "",
                email: staffMember.email ?? "",
                licensePlate: staffMember.licenseplate ?? "",
                receivesCustomerAlerts: !!staffMember.receivescustomeralerts,
                receivesOwnerMessages: !!staffMember.receivesownermessages,
                receivesAdminMessages: !!staffMember.receivesadminmessages,
                autoCheckinWithANPR: !!staffMember.autocheckinwithanpr,
                usesWhatsappForAlerts: !!staffMember.useswhatsappforalerts,
                canAccessReports: !!staffMember.canaccessreports,
                canAccessAdministration: !!staffMember.canaccessadministration,
                role: staffMember.role,
            }
        });
    }, []);

    const handleDeleteStaff = useCallback(async (staffMember: Staff) => {
        showToast({
            message: "",
            confirmMessage: t("administration.locationAdministration.deleteStaffConfirmation", { name: staffMember.name }),
            type: ToastType.CONFIRM,
            confirmCallback: async () => {
                try {
                    await deleteStaffMember(staffMember.id);
                    showToast({
                        message: t("administration.locationAdministration.staffDeletedSuccessfully", { name: staffMember.name }),
                        type: ToastType.SUCCESS,
                    });
                } catch (error) {
                    showToast({
                        message: t("common.unknownError"),
                        type: ToastType.ERROR,
                    });
                }
            },
        });
    }, [t, showToast, deleteStaffMember]);

    const openPasswordModal = useCallback((staffMember: Staff) => {
        setPasswordModalInfo({
            isOpen: true,
            staffMember,
        });
    }, []);

    const closePasswordModal = useCallback(() => {
        setPasswordModalInfo({
            isOpen: false,
        });
    }, []);

    return (
        <>
            <SurfaceCard
                title={
                    <Typography variant="h4">
                        {t("administration.locationAdministration.team")}
                    </Typography>
                }
                action={
                    <Tooltip title={t("administration.locationAdministration.addStaff")}>
                        <IconButton
                            size="small"
                            onClick={() => setStaffDialogInfo({ isOpen: true, mode: DialogMode.CREATE })}
                        >
                            <AddIcon />
                        </IconButton>
                    </Tooltip>
                }
            >
                <EntityListBox>
                    {staffMembers
                        .slice()
                        // put current user at the top of the list
                        .sort((a, b) => {
                            const aIsCurrentUser = a.id === userInfo?.userId;
                            const bIsCurrentUser = b.id === userInfo?.userId;
                            if (aIsCurrentUser) return -1;
                            if (bIsCurrentUser) return 1;
                            return 0;
                        })
                        .map((staffMember) => {
                            const isCurrentUser = staffMember.id === userInfo?.userId;
                            return (
                                <EntityCard
                                    key={staffMember.phonenumber}
                                    title={staffMember.name}
                                    subtitle={staffMember.email}
                                    avatar={{
                                        backgroundColor: stringToHex(staffMember.name),
                                    }}
                                    badge={{
                                        label: t(`common.role.${staffMember.role.toLowerCase()}`),
                                        color: "primary",
                                        variant: "outlined",
                                    }}
                                    hoverActions={[
                                        {
                                            icon: <VisibilityIcon />,
                                            label: t("common.viewDetails"),
                                            onClick: () => openStaffDialog(staffMember, DialogMode.VIEW),
                                        },
                                        {
                                            icon: <EditIcon />,
                                            label: t("common.edit"),
                                            onClick: () => openStaffDialog(staffMember, DialogMode.EDIT),
                                        },
                                        {
                                            icon: <LockIcon />,
                                            label: t("common.changePassword"),
                                            onClick: () => openPasswordModal(staffMember),
                                        },
                                        ...(isCurrentUser ? [] : [{
                                            icon: <DeleteIcon />,
                                            label: t("common.delete"),
                                            onClick: () => handleDeleteStaff(staffMember),
                                        }]),
                                    ]}
                                    isHighlighted={isCurrentUser}
                                />
                            );
                        })}
                </EntityListBox>
            </SurfaceCard>
            {
                staffDialogInfo.isOpen && (
                    <StaffDialog
                        open={staffDialogInfo.isOpen}
                        mode={staffDialogInfo.mode}
                        initialValues={staffDialogInfo.dialogData}
                        onClose={closeDialog}
                        isCurrentUser={staffDialogInfo.dialogData?.staffId === userInfo?.userId}
                    />
                )
            }
            <ChangePasswordDialog
                open={passwordModalInfo.isOpen}
                staffMember={passwordModalInfo.staffMember}
                onClose={closePasswordModal}
            />
        </>
    );
}

export default Team;
